import { useEffect, useState } from 'react';
import { useNotification, useTranslate, useUpdate } from '@refinedev/core';
import type { HttpError } from '@refinedev/core';
import { Alert, Form, Input, Modal } from 'antd';
import type {
  ContentImprintOut,
  ContentImprintUpdateIn,
} from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';

interface ImprintRenameModalProps {
  open: boolean;
  onClose: () => void;
  imprint: ContentImprintOut;
}

export const ImprintRenameModal = ({
  open,
  onClose,
  imprint,
}: ImprintRenameModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync } = useUpdate<ContentImprintOut>();
  const [form] = Form.useForm<ContentImprintUpdateIn>();
  const [submitting, setSubmitting] = useState(false);

  const tk = (suffix: string) => t(`content_catalog.imprints.rename.${suffix}`);

  useEffect(() => {
    if (open) {
      form.resetFields();
    } else {
      setSubmitting(false);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    let values: ContentImprintUpdateIn;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      await mutateAsync({
        resource: 'content_catalog.imprints',
        id: imprint.id,
        values: { name: values.name.trim() },
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      onClose();
    } catch (error) {
      const message = extractBackendMessage(error) ?? tk('error_fallback');
      // A name the publisher already has belongs next to the field.
      if ((error as HttpError).statusCode === 409) {
        form.setFields([{ name: 'name', errors: [message] }]);
      } else {
        notify?.({
          type: 'error',
          message: t('notifications.error'),
          description: message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
    >
      {/* Products keep their link, but the import resolves imprints by name. */}
      <Alert
        type="warning"
        showIcon
        message={tk('hints.sheet_name')}
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
        initialValues={{ name: imprint.name }}
      >
        <Form.Item
          name="name"
          label={t('content_catalog.imprints.fields.name')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t('content_catalog.imprints.errors.name_required'),
            },
            {
              max: 512,
              message: t('content_catalog.imprints.errors.max_length', {
                max: 512,
              }),
            },
          ]}
        >
          <Input autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
};
